import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

const formatEnrollmentType = (type) => {
  if (typeof type !== "string" || !type) {
    return "";
  }

  return type.charAt(0).toUpperCase() + type.slice(1);
};

const formatPrice = (price) => {
  if (price === null || price === undefined || price === "") {
    return "N/A";
  }

  const numericPrice = Number(price);
  if (Number.isNaN(numericPrice)) {
    return "N/A";
  }

  return `PKR ${numericPrice.toLocaleString()}`;
};

const mapAssignedCourse = (course) => ({
  id: course?._id,
  title: course?.courseTitle || "Untitled Course",
  category: course?.courseCategory?.categoryName || "Uncategorized",
  enrollmentType: formatEnrollmentType(course?.courseEnrollementType),
  price: formatPrice(course?.coursePrice),
  instructorsCount: Array.isArray(course?.assignedInstructors)
    ? course.assignedInstructors.length
    : 0,
});

const InfoIcon = () => (
  <svg width="32" height="32" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
      fill="#9CA3AF"
    />
  </svg>
);

const AssignedCourseCard = ({ course, onViewDetails }) => (
  <article className="rounded-[7px] border border-[#D9D9D9A1] bg-white px-3 pb-[11px] pt-2.5 sm:px-[18px]">
    <div className="flex items-start justify-between gap-2 sm:gap-3">
      <div className="min-w-0">
        <h4 className="text-lg font-medium leading-[26px] text-black sm:text-[22px]">
          {course.title}
        </h4>
        <p className="mt-[5px] text-base leading-[22px] text-[#595959] sm:text-[19px]">
          Category: {course.category}
          {course.enrollmentType ? ` \u2022 ${course.enrollmentType}` : ""}
        </p>
      </div>
      <span className="shrink-0 rounded-full border border-[#589FF2] bg-[#589FF233] px-2.5 py-[3px] text-xs leading-[14px] text-[#589FF2] sm:px-[12.5px]">
        {course.price}
      </span>
    </div>

    <div className="mt-1 h-[19px] text-right">
      <button
        type="button"
        className="text-sm leading-[19px] text-[#923D8C] sm:text-base"
        onClick={() => onViewDetails?.(course)}
      >
        View Course Details
      </button>
    </div>
  </article>
);

const InstructorAssignedCourses = ({ courses = [], onViewDetails }) => {
  const navigate = useNavigate();

  const mappedCourses = useMemo(
    () => (Array.isArray(courses) ? courses.map(mapAssignedCourse) : []),
    [courses]
  );

  const hasCourses = mappedCourses.length > 0;

  const handleViewDetails = (course) => {
    if (onViewDetails) {
      onViewDetails(course);
      return;
    }

    if (course?.id) {
      navigate(`/admin/course-management/course-detail/${course.id}`);
    }
  };

  return (
    <section className="rounded-xl border border-[#60555557] bg-white px-4 pb-4 pt-[22px] sm:px-6">
      <h3 className="text-xl font-medium leading-[23px] text-black">
        Assigned Courses
      </h3>

      {hasCourses ? (
        <div className="mt-[13px] space-y-1.5">
          {mappedCourses.map((course) => (
            <AssignedCourseCard
              key={course.id}
              course={course}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      ) : (
        <div className="mt-4 flex flex-col items-center justify-center gap-2 rounded-lg bg-[#F9FAFB] px-4 py-8 text-center">
          <InfoIcon />
          <p className="text-base leading-[19px] text-[#6B7280]">
            No courses assigned yet.
          </p>
        </div>
      )}
    </section>
  );
};

export default InstructorAssignedCourses;
